import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";

import {
  FaFacebookF,
  FaInstagram,
  FaHeadset,
  FaEnvelopeOpenText,
  FaMapMarkedAlt,
  FaLinkedinIn,
  FaWhatsapp,
  FaChevronDown,
  FaQuestionCircle,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import "./page-banner.css";

const faqIndexes = Array.from({ length: 10 }, (_, index) => index);

const ContactPage = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openFaqs, setOpenFaqs] = useState([null, null]);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqIndexes.map((faqIndex) => ({
      "@type": "Question",
      name: t(`contactPage.faqs.${faqIndex}.question`),
      acceptedAnswer: {
        "@type": "Answer",
        text: t(`contactPage.faqs.${faqIndex}.answer`),
      },
    })),
  };

  const renderFaqColumn = (items, columnIndex) => (
    <div className="space-y-4">
      {items.map((faqIndex, itemIndex) => {
        const isOpen = openFaqs[columnIndex] === itemIndex;
        const questionKey = `contactPage.faqs.${faqIndex}.question`;
        const answerKey = `contactPage.faqs.${faqIndex}.answer`;

        return (
          <div
            key={faqIndex}
            className={`group rounded-2xl border border-black/10 bg-white/80 p-5 shadow-[0_10px_35px_rgba(25,25,25,0.05)] transition duration-300 ${isOpen ? "border-[#e47203]/50 bg-white shadow-[0_16px_40px_rgba(228,114,3,0.12)]" : ""}`}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => {
                setOpenFaqs((current) => {
                  const next = [...current];
                  next[columnIndex] = isOpen ? null : itemIndex;
                  return next;
                });
              }}
              className="flex w-full items-center justify-between gap-5 text-left text-base font-semibold leading-6 text-[#252525]"
            >
              <span>{t(questionKey)}</span>
              <FaChevronDown
                aria-hidden="true"
                className={`shrink-0 text-sm text-[#e47203] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isOpen && (
              <p className="max-w-xl pt-4 text-sm leading-7 text-gray-600">
                {t(answerKey)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setError(t("contactPage.error"));
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    const templateParams = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
      time: new Date().toLocaleString(),
    };

    // 1️⃣ Send Admin Email
    emailjs
      .send(
        "service_fn9fojz",         // ✅ Your SMTP Service ID
        "template_2byg5lq",        // ✅ Admin TEMPLATE ID
        templateParams,
        "8E06E7D8CV2BJbLbR"       // ✅ PUBLIC KEY
      )
      .then(
        () => {
          // 2️⃣ Send Thank You Email to Customer
          emailjs
            .send(
              "service_fn9fojz",
              "template_s89yuju",   // ✅ Thank You TEMPLATE ID
              templateParams,
              "8E06E7D8CV2BJbLbR"
            )
            .then(
              () => {
                setSuccess(t("contactPage.success"));
                setFormData({ name: "", email: "", message: "" });
                setLoading(false);
              },
              (err) => {
                console.error("EmailJS Thank You Error:", err);
                setError(t("contactPage.thankYouError"));
                setLoading(false);
              }
            );
        },
        (err) => {
          console.error("EmailJS Admin Error:", err);
          setError(t("contactPage.sendError"));
          setLoading(false);
        }
      );
  };
  return (
    <>
      <Helmet>
        <title>Contact Harisharan Devgan | Official Website</title>
        <meta
          name="description"
          content="Get in touch with Harisharan Devgan's team for enquiries, media interactions, professional associations, collaborations and other official communications."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://harisharandevgan.com/connect/" />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Harisharan Devgan | Official Website" />
        <meta
          property="og:description"
          content="Get in touch with Harisharan Devgan's team for enquiries, media interactions, professional associations, collaborations and other official communications."
        />
        <meta property="og:url" content="https://harisharandevgan.com/connect/" />
        <meta property="og:site_name" content="Harisharan Devgan" />
        <meta property="og:updated_time" content="2026-02-01T11:53:28+05:30" />
        <meta
          property="og:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta
          property="og:image:secure_url"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="576" />
        <meta property="og:image:alt" content="Connect" />
        <meta property="og:image:type" content="image/webp" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Harisharan Devgan | Official Website" />
        <meta
          name="twitter:description"
          content="Get in touch with Harisharan Devgan's team for enquiries, media interactions, professional associations, collaborations and other official communications."
        />
        <meta
          name="twitter:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta name="twitter:label1" content="Time to read" />
        <meta name="twitter:data1" content="Less than a minute" />

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        {/* Favicons / Icons */}
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />

      </Helmet>
      {/* ================= BANNER ================= */}
      <section className="relative min-h-[400px] flex items-center mt-[98px] overflow-hidden">
        {/* 🚀 LCP / Hero Image */}
        <img
          src="/images/contact-banner.webp"
          alt={t("contactPage.bannerAlt")}

          /* Performance */
          loading="eager"
          fetchpriority="high"
          decoding="async"

          /* CLS Fix */
          width="1920"
          height="600"

          className="page-banner-image page-banner-image--connect absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative max-w-7xl mx-auto w-full px-6 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <div>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-white mb-3"
              style={{ letterSpacing: "3px" }}
            >
              {t("contactPage.title")}
            </h1>
            <p className="text-lg text-gray-300">
              {t("contactPage.subtitle")}
            </p>
          </div>

          <div className="md:text-right">
            <h2 className="text-lg font-semibold text-white">
              <Link
                to="/"
                className="text-gray-300 hover:text-orange-500 transition"
              >
                {t("commonContact.home")}
              </Link>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-orange-500">
                {t("contactPage.breadcrumb")}
              </span>
            </h2>
          </div>
        </div>
      </section>

      {/* ================= CONTACT FORM ================= */}
      <section className="bg-white py-[120px]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative bg-white rounded-[25px] shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12">

              {/* LEFT FORM */}
              <form
                onSubmit={handleSubmit}
                className="md:col-span-10 p-12 md:pr-[340px]"
              >
                <h3 className="text-black text-[40px] font-semibold mb-2 custom">
                  {t("contactPage.formTitle")}
                </h3>

                <p className="text-black text-[13px] mb-8">
                  {t("contactPage.formSubtitle")}
                </p>

                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                {success && (
                  <p className="text-green-600 text-sm mb-4">{success}</p>
                )}

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("contactPage.name")}
                  className="w-full border-b border-gray-300 outline-none py-2 mb-6 placeholder:text-xs focus:border-indigo-700"
                />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t("contactPage.email")}
                  className="w-full border-b border-gray-300 outline-none py-2 mb-6 placeholder:text-xs focus:border-indigo-700"
                />

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t("contactPage.message")}
                  rows="4"
                  className="w-full border-b border-gray-300 outline-none py-2 mb-6 placeholder:text-xs focus:border-indigo-700 resize-none"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-4 py-3 rounded-full text-white text-sm tracking-widest bg-[#ff6900] hover:opacity-90 transition disabled:opacity-50"
                >
                  {loading ? t("contactPage.sending") : t("contactPage.submit")}
                </button>
              </form>

              {/* RIGHT SOCIAL STRIP */}
              <div className="md:col-span-2 flex items-end justify-center bg-[#ff6900] pt-4">
                <ul className="flex gap-4 pb-6 text-white text-sm">
                  <li>
                    <a href="https://www.facebook.com/Harisharaandevgan/" target="_blank" rel="noopener noreferrer">
                      <FaFacebookF />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.instagram.com/harisharandevgan/" target="_blank" rel="noopener noreferrer">
                      <FaInstagram />
                    </a>
                  </li>
                  <li>
                    <a href="https://twitter.com/harisharandevgn" target="_blank" rel="noopener noreferrer">
                      <FaXTwitter />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.linkedin.com/in/harisharandevgan/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedinIn />
                    </a>
                  </li>
                  <li>
                    <a href="https://whatsapp.com/channel/0029Vb6kC1aLtOjGkaVl4i1L" target="_blank" rel="noopener noreferrer">
                      <FaWhatsapp />
                    </a>
                  </li>
                </ul>

              </div>
            </div>

            {/* FLOATING CONTACT INFO */}
            <div
              className="hidden md:flex absolute top-[18%] right-0 h-[340px] w-[340px]
              bg-[#2d2d2d] text-white p-10 rounded-l-[25px]
              flex-col justify-center"
            >
              <h4 className="tracking-wide mb-6 text-lg font-semibold">
                {t("contactPage.infoTitle")}
              </h4>

              <div className="flex items-center gap-4 mb-6 text-sm">
                <FaHeadset /> +91 9811300707
              </div>

              <div className="flex items-center gap-4 mb-6 text-sm">
                <FaEnvelopeOpenText /> mail@harisharandevgan.com
              </div>

              <div className="flex items-center gap-4 text-sm">
                <FaMapMarkedAlt /> INDIA | QATAR | USA
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="relative overflow-hidden bg-[#f7f7f5] py-24">
        <div className="pointer-events-none absolute -right-32 top-12 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="pointer-events-none absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-slate-200/50 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-6">
          <div className="mb-12 max-w-2xl">
            <div className="mb-4 flex items-center gap-3 text-xs font-bold normal-case tracking-[0.28em] text-[#e47203]">
              <FaQuestionCircle aria-hidden="true" />
              <span>{t("contactPage.faqLabel")}</span>
            </div>
            <h2 className="font-serif text-4xl font-semibold leading-tight text-[#202020] md:text-5xl">
              {t("contactPage.faqHeading")}
            </h2>
            {/* <p className="mt-5 max-w-xl text-base leading-7 text-gray-600">
             Curious about Harisharan Devgan’s journey, work in organic farming, approach to sustainability, or opportunities to connect?
            </p> */}
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:items-start">
            {renderFaqColumn(faqIndexes.slice(0, 5), 0)}
            {renderFaqColumn(faqIndexes.slice(5), 1)}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
